/**
 * EvidenceLinkChip — one line of a lane-v2 answer's Evidence section, as a control.
 *
 * A lane-v2 answer ends with a code-written list of what it read, each line a
 * `[kind](artifact://<id>)` link ("Source code: AccountService", "Query result 2").
 * The default markdown renderer shows such a link as plain text, because it has no
 * artifact to open: lane v2 carries no artifact rows in its store, only the id in
 * the link. This chip is the missing half. Collapsed it is the same pill the legacy
 * answer uses (`CitationChip`); on click it fetches the artifact by that id and opens
 * the SAME `ArtifactCard` — so a source read opens as code and a query result as its
 * query and table, with no second viewer to keep in step.
 *
 * WHY IT FETCHES ON CLICK AND NOT ON MOUNT. An answer lists up to 40 files and a
 * source file can be hundreds of kilobytes. Nothing is requested until the reader
 * asks for one; the card is then handed what was fetched (`initialData`), so opening
 * costs one request, not two.
 *
 * A FILE THAT CANNOT BE OPENED IS SAID SO, ON THE CHIP. The artifact route answers
 * only for the reader's own runs; a purged or foreign id is a 404. The chip then
 * keeps its label and reads "unavailable" — it never disappears and never throws,
 * and a second click tries again (a transient failure looks the same from here).
 *
 * The state rule is a pure function (`nextEvidenceChipState`) and the markup is a
 * props-only view (`EvidenceLinkChipView`), following this suite's node-environment
 * tests: the rule is proven as logic, the view by static render.
 */
import { useCallback, useEffect, useReducer } from "react";
import { FileText, Loader2 } from "lucide-react";
import { ArtifactCard } from "@/components/molecules/multi-agent/ArtifactCard";
import { getArtifact } from "@/services/multi-agent/api";
import type { ArtifactContent } from "@/services/multi-agent/types";

const SCHEME = "artifact://";

/** The artifact id of an `artifact://<id>[?…]` href (also its `#artifact://` hash form), or null for any other href. */
export function artifactIdOfHref(href: string | null | undefined): string | null {
  if (typeof href !== "string") return null;
  const bare = href.startsWith(`#${SCHEME}`) ? href.slice(1) : href;
  if (!bare.startsWith(SCHEME)) return null;
  const id = bare.slice(SCHEME.length).split("?")[0].trim();
  return id || null;
}

/** The row `ArtifactCard` renders from. The label the answer shows wins over the stored name: it is what the reader clicked. */
export function evidenceCardRow(id: string, label: string, content: ArtifactContent) {
  const created = content.created_at ? Date.parse(content.created_at) : NaN;
  return {
    id,
    type: content.type || "",
    name: label.trim() || content.name || id.slice(0, 8),
    toolName: content.tool_name || "",
    byteSize: typeof content.byte_size === "number" ? content.byte_size : 0,
    createdAt: Number.isFinite(created) ? created : 0,
    intake: null,
  };
}

export type EvidenceChipState =
  | { kind: "idle"; data: ArtifactContent | null; failed: boolean }
  | { kind: "loading" }
  | { kind: "open"; data: ArtifactContent };

export type EvidenceChipEvent =
  | { type: "click" }
  | { type: "loaded"; data: ArtifactContent }
  | { type: "failed" }
  | { type: "collapse" };

export const EVIDENCE_CHIP_IDLE: EvidenceChipState = { kind: "idle", data: null, failed: false };

/**
 * The one rule. A click opens what is already fetched, else starts ONE fetch (a click while
 * loading is ignored, so a double click cannot start two). Collapsing keeps what was fetched.
 * A late answer for a chip that is no longer loading is dropped.
 */
export function nextEvidenceChipState(state: EvidenceChipState, event: EvidenceChipEvent): EvidenceChipState {
  switch (event.type) {
    case "click":
      if (state.kind !== "idle") return state;
      return state.data ? { kind: "open", data: state.data } : { kind: "loading" };
    case "loaded":
      return state.kind === "loading" ? { kind: "open", data: event.data } : state;
    case "failed":
      return state.kind === "loading" ? { kind: "idle", data: null, failed: true } : state;
    case "collapse":
      return state.kind === "open" ? { kind: "idle", data: state.data, failed: false } : state;
  }
}

/** Link text as a string, for the control's name (the children of a markdown link are text nodes). */
export function evidenceLabelText(children: React.ReactNode): string {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(evidenceLabelText).join("");
  return "";
}

const PILL =
  "ma-citation-chip ma-interactive inline-flex max-w-[min(100%,320px)] cursor-pointer items-center gap-1.5 rounded-full border border-ma-accent-faint bg-ma-accent-soft px-2.5 py-0.5 align-middle text-[11px] font-medium leading-[1.3] text-ma-accent my-0.5";

interface ViewProps {
  artifactId: string;
  label: string;
  state: EvidenceChipState;
  onClick: () => void;
  onCollapse: () => void;
}

export function EvidenceLinkChipView({ artifactId, label, state, onClick, onCollapse }: ViewProps) {
  const name = label.trim() || artifactId.slice(0, 8);
  if (state.kind === "open") {
    return (
      <div className="ma-artifact-block my-2 w-full">
        <ArtifactCard
          artifact={evidenceCardRow(artifactId, name, state.data)}
          initialData={state.data}
          defaultOpen
          onCollapse={onCollapse}
          hideWorkspaceActions
        />
      </div>
    );
  }
  const loading = state.kind === "loading";
  const failed = state.kind === "idle" && state.failed;
  const title = loading ? `Opening ${name}` : failed ? `${name} could not be opened. Select to try again.` : `Open ${name}`;
  return (
    <button
      type="button"
      onClick={onClick}
      className={PILL}
      title={title}
      aria-label={title}
      aria-busy={loading || undefined}
      data-evidence-artifact={artifactId}
    >
      {loading ? (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin opacity-70" aria-hidden />
      ) : (
        <FileText className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
      )}
      <span className="max-w-[240px] truncate font-normal">{name}</span>
      {failed && <span className="font-normal opacity-70">· unavailable</span>}
    </button>
  );
}

export function EvidenceLinkChip({ artifactId, children }: { artifactId: string; children?: React.ReactNode }) {
  const [state, dispatch] = useReducer(nextEvidenceChipState, EVIDENCE_CHIP_IDLE);
  const loading = state.kind === "loading";

  useEffect(() => {
    if (!loading) return;
    let cancelled = false;
    getArtifact(artifactId).then(
      (data) => { if (!cancelled) dispatch({ type: "loaded", data }); },
      () => { if (!cancelled) dispatch({ type: "failed" }); },
    );
    return () => { cancelled = true; };
  }, [loading, artifactId]);

  const onClick = useCallback(() => dispatch({ type: "click" }), []);
  const onCollapse = useCallback(() => dispatch({ type: "collapse" }), []);
  return (
    <EvidenceLinkChipView
      artifactId={artifactId}
      label={evidenceLabelText(children)}
      state={state}
      onClick={onClick}
      onCollapse={onCollapse}
    />
  );
}

/** What `ArtifactLinkContext` is given for a lane-v2 transcript: every artifact link of an answer is a chip. */
export function renderEvidenceLink(artifactId: string, children: React.ReactNode): React.ReactNode {
  return <EvidenceLinkChip artifactId={artifactId}>{children}</EvidenceLinkChip>;
}
